// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import type { Seat } from '../data.ts';
import { GROUP_META } from '../parties.ts';
import { fmtPct, fmtSigned, esc, escTip, fullName } from '../utils/format.ts';

interface Metric {
  key: string;
  label: string;
  desc: string;
  value: (s: Seat) => number;
  fmt: (s: Seat) => string;
  dir: 'asc' | 'desc';
  color: (s: Seat) => string;
  skipNullSwing?: boolean;
}

const METRICS: Metric[] = [
  { key: 'marginal', label: 'Most marginal', desc: 'Seats decided by the smallest two-candidate margin — the ones that could go either way next time.', value: (s) => s.marginPct, fmt: (s) => `${s.marginPct.toFixed(s.marginPct < 0.1 ? 2 : 1)}%`, dir: 'asc', color: (s) => GROUP_META[s.winnerGroup].color },
  { key: 'safest', label: 'Safest seats', desc: 'The biggest two-candidate margins — fortress seats.', value: (s) => s.marginPct, fmt: (s) => `${s.marginPct.toFixed(1)}%`, dir: 'desc', color: (s) => GROUP_META[s.winnerGroup].color },
  { key: 'swingALP', label: 'Biggest swing to Labor', desc: 'Largest two-party swing towards Labor since 2022 (seats with a comparable 2022 result).', value: (s) => s.tppSwingToALP ?? -999, fmt: (s) => fmtSigned(s.tppSwingToALP as number), dir: 'desc', color: () => GROUP_META.ALP.color, skipNullSwing: true },
  { key: 'swingCOA', label: 'Biggest swing to Coalition', desc: 'Largest two-party swing towards the Coalition — the seats that bucked the trend.', value: (s) => s.tppSwingToALP ?? 999, fmt: (s) => fmtSigned(s.tppSwingToALP as number), dir: 'asc', color: () => GROUP_META.COA.color, skipNullSwing: true },
  { key: 'turnoutLow', label: 'Lowest turnout', desc: 'Where the fewest enrolled voters cast a ballot.', value: (s) => s.turnoutPct, fmt: (s) => fmtPct(s.turnoutPct), dir: 'asc', color: () => '#64748b' },
  { key: 'informalHigh', label: 'Highest informal vote', desc: 'Where the most ballots could not be counted.', value: (s) => s.informalPct, fmt: (s) => fmtPct(s.informalPct), dir: 'desc', color: () => GROUP_META.OTH.color },
];

export function renderRankings(root: HTMLElement, ctx: Ctx): void {
  let metric = METRICS[0];

  root.innerHTML = `
    <div class="view-head">
      <h2>Rankings</h2>
      <p class="subtitle">The seats at the extremes of every measure. Pick a metric; click any seat for its full result.</p>
    </div>
    <div class="panel">
      <div class="controls">
        <div class="seg" id="metric-seg">
          ${METRICS.map((m, i) => `<button data-i="${i}"${i === 0 ? ' class="active"' : ''}>${m.label}</button>`).join('')}
        </div>
      </div>
      <div class="panel-sub" id="metric-desc"></div>
      <div id="rank-list"></div>
    </div>`;

  const list = root.querySelector('#rank-list')!;
  const descEl = root.querySelector('#metric-desc')!;

  const draw = () => {
    descEl.textContent = metric.desc;
    const pool = metric.skipNullSwing ? ctx.data.seats.filter((s) => s.tppSwingToALP != null) : ctx.data.seats;
    const sorted = [...pool].sort((a, b) =>
      metric.dir === 'asc' ? metric.value(a) - metric.value(b) : metric.value(b) - metric.value(a)).slice(0, 25);
    const vals = sorted.map((s) => Math.abs(metric.value(s)));
    const max = Math.max(...vals, 0.0001);
    list.innerHTML = sorted.map((s, i) => {
      const w = (Math.abs(metric.value(s)) / max) * 100;
      return `<div class="barrow rank-row" role="button" tabindex="0" data-seat="${esc(s.name)}" data-tip="${escTip(`${s.name} (${s.state})\n${fullName(s.member.given, s.member.surname)} — ${GROUP_META[s.member.group].label}`)}">
        <span class="lab"><span style="color:var(--text-tertiary);font-variant-numeric:tabular-nums">${i + 1}.</span> ${esc(s.name)} <span style="color:var(--text-tertiary);font-weight:400">${esc(s.state)}</span></span>
        <span class="track"><span class="fill" style="width:${w}%;background:${metric.color(s)}"></span></span>
        <span class="val">${metric.fmt(s)}</span>
      </div>`;
    }).join('');
    list.querySelectorAll<HTMLElement>('.rank-row').forEach((el) => {
      el.addEventListener('click', () => ctx.openSeat(el.dataset.seat!));
      el.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') ctx.openSeat(el.dataset.seat!); });
    });
  };

  root.querySelectorAll<HTMLButtonElement>('#metric-seg button').forEach((b) => {
    b.addEventListener('click', () => {
      metric = METRICS[Number(b.dataset.i)];
      root.querySelectorAll('#metric-seg button').forEach((x) => x.classList.toggle('active', x === b));
      draw();
    });
  });
  draw();
}
